/* eslint-disable react/no-this-in-sfc */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import moment from 'moment';

import RightModalLayout from '../layouts/Modal/RightModalLayout';
import FormProviderMC from '../components/FormProviderMC';
import { SIDEBAR_ACTIONS } from '../../constants/actionsConstants';
import DropdownAsyncMC from '../components/DropdownAsyncMC';
import DropdownMC from '../components/DropdownMC';
import InputComponent from '../components/InputMC';
import DatePickerMC from '../components/DatePickerMC';
import RadioButtonSelectorMC from '../components/RadioButtonSelectorMC';
import CustomersModelMc from '../models/CustomersModelMc';
import { fetchActiveAgents } from '../../actions/agents';
import { createNewDeal, createNewCustomerAndDeal, updateFunnelStepDeal } from '../../actions/funnels';
import ButtonComponent from '../components/ButtonMC';
import TitleMC from '../components/TitleMC';

/**
 * An object that maps temperature states to their corresponding CSS classes.
 * @type {Object}
 * @property {string} cold - CSS class for cold temperature state.
 * @property {string} warm - CSS class for warm temperature state.
 * @property {string} hot - CSS class for hot temperature state.
 */
const tempClasses = {
  cold: '!tw-text-blue-400',
  warm: '!tw-text-yellow-500',
  hot: '!tw-text-red-400'
};

/**
 * An array of attribute names for a deal.
 * @type {string[]}
 */
const attributes = ['id', 'email', 'first_name', 'last_name', 'phone', 'username', 'whatsapp_name'];

/**
 * Generates a label for a customer based on their first name, last name, phone, and email.
 *
 * @param {Object} customer - The customer object.
 * @param {string} [customer.first_name] - The first name of the customer.
 * @param {string} [customer.last_name] - The last name of the customer.
 * @param {string} [customer.phone] - The phone number of the customer.
 * @param {string} [customer.email] - The email address of the customer.
 * @param {string} [customer.username] - The username of the customer.
 * @param {string} [customer.whatsapp_name] - The WhatsApp name of the customer.
 * @returns {string} A formatted label for the customer.
 */
const getLabelCustomer = (customer) => {
  const fullName = `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim();
  const identity = fullName
    || customer?.whatsapp_name
    || customer?.username
    || customer?.email
    || customer?.phone
    || '';

  if (!identity) return '';

  const contact = customer?.phone || customer?.email;
  return contact && fullName ? `${fullName} - (${contact})` : identity;
};

/**
 * Asynchronously loads options for a select input based on the provided search text.
 *
 * @param {Object} params - The parameters for the search.
 * @param {string} params.text - The search text to query customers.
 * @returns {Promise<Object>} A promise that resolves to an object containing the customer data.
 * @returns {Array<Object>} returns.data - An array of customer objects.
 * @returns {string} returns.data[].id - The ID of the customer.
 * @returns {string} returns.data[].label - The label for the customer, generated by getLabelCustomer function.
 */
const loadOptions = async ({ text }) => {
  const response = await CustomersModelMc.search({ text, attributes });
  return {
    data: response?.customers?.map((customer) => ({
      id: customer.id,
      label: getLabelCustomer(customer)
    })) || []
  };
};

/**
 * ManageDeal component handles the creation and updating of deals within a selected funnel.
 * It uses React hooks for state management and Redux for dispatching actions and accessing the store.
 * The component also utilizes the react-hook-form library for form handling and validation with Yup schema.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.selectedFunnel - The selected funnel object containing funnel steps.
 * @param {Object} props.deal - The deal object containing deal details.
 * @param {Function} props.reloadDeals - Function to reload the list of deals after creation or update.
 * @param {boolean} [props.externalCreate=false] - Flag indicating if the deal is being created externally.
 *
 * @returns {JSX.Element} The rendered ManageDeal component.
 */
const ManageDeal = ({ selectedFunnel, deal, reloadDeals, externalCreate = false }) => {
  const { t } = useTranslation('Funnels');
  const dispatch = useDispatch();
  const { agents } = useSelector((reduxState) => reduxState.agentsReducer);
  const { retailer_info } = useSelector((reduxState) => reduxState.retailerUsersReducer);
  const { currentRetailerUser } = useSelector((reduxState) => reduxState.mainReducer);

  const { funnels } = useSelector((reduxState) => reduxState.funnelsReducer);

  const isEditing = Boolean(deal?.id);
  const idempotencyKey = useMemo(() => uuidv4(), []);
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedFunnelId, setSelectedFunnelId] = useState(selectedFunnel?.id);
  const [optionFunnelSteps, setOptionFunnelSteps] = useState([]);
  const [agentsOptions, setAgentsOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(deal?.customer);
  const [closeReasons, setCloseReasons] = useState([]);

  const closeModal = useCallback(() => {
    dispatch({
      type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR,
      payload: { componentKey: 'ManageDeal' }
    });
  }, [dispatch]);

  const schema = object().shape({
    funnel_step_id: string().required(t('deal.stage.required')),
    customer_id: string().required(t('deal.customer.required')),
    name: string().required(t('deal.negotiation.required')).max(100, t('deal.negotiation.maxLength')),
    amount: string().nullable().transform((value, originalValue) => (originalValue === '' ? null : value))
      .test('is-number', t('deal.amount.invalid'), (value) => value === null || value === undefined || (!isNaN(value) && Number(value) >= 0)),
    opening_date: string().nullable().transform((value, originalValue) => (originalValue === '' ? null : value))
      .test('is-date', t('deal.openingDate.invalid'), (value) => value === null || value === undefined || !isNaN(Date.parse(value))),
    expected_close_date: string().nullable().transform((value, originalValue) => (originalValue === '' ? null : value))
      .test('is-date', t('deal.expectedCloseDate.invalid'), (value) => value === null || value === undefined || !isNaN(Date.parse(value)))
      .test('is-after-opening-date', t('deal.expectedCloseDate.invalidOrder'), function (value) {
        const { opening_date } = this.parent;
        if (opening_date && value) {
          const opening = new Date(opening_date);
          const expected = new Date(value);
          opening.setHours(0, 0, 0, 0);
          expected.setHours(0, 0, 0, 0);
          return opening <= expected;
        }
        return true;
      })
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      deal_id: deal?.id || undefined,
      funnel_step_id: deal?.funnel_step_id || '',
      customer_id: deal?.customer?.id || deal?.customer_id || '',
      name: deal?.name || '',
      amount: deal?.amount || 0,
      opening_date: deal?.opening_date ? new Date(deal.opening_date).toLocaleString("en-US", { timeZone: "UTC" }) : new Date(),
      expected_close_date: deal?.expected_close_date ? new Date(deal.expected_close_date).toLocaleString("en-US", { timeZone: "UTC" }) : '',
      lead_status: deal?.lead_status || undefined,
      retailer_id: retailer_info.id,
      retailer_user_id: deal?.agent?.id || deal?.retailer_user_id || (isEditing ? '' : currentRetailerUser?.id),
      record_id: deal?.record_id || undefined,
      close_reason: deal?.reason_closure_id || ''
    }
  });

  const submitAction = async (dataDeal) => {
    if (submittingRef.current) return;

    const dealBody = {
      ...dataDeal,
      customer_id: String(dataDeal.customer_id) === '0' ? undefined : dataDeal.customer_id,
      retailer_user_id: dataDeal.retailer_user_id === '' ? undefined : dataDeal.retailer_user_id,
      opening_date: dataDeal.opening_date && dataDeal.opening_date !== '' && moment(dataDeal.opening_date).format('YYYY-MM-DD'),
      expected_close_date: dataDeal.expected_close_date && dataDeal.expected_close_date !== '' && moment(dataDeal.expected_close_date).format('YYYY-MM-DD')
    };

    submittingRef.current = true;
    setSubmitting(true);

    try {
      const stepObj = currentFunnel?.funnel_steps?.find((step) => String(step.id) === String(dealBody.funnel_step_id));
      const _columnWebId = stepObj?.web_id;
      let actionPromise;
      if (isEditing) {
        dealBody.funnel_step_id = _columnWebId;
        actionPromise = dispatch(updateFunnelStepDeal(dealBody));
      } else {
        dealBody.idempotency_key = idempotencyKey;
        if (selectedCustomer && dealBody.customer_id === undefined) {
          actionPromise = dispatch(createNewCustomerAndDeal({
            deal: dealBody,
            customer: selectedCustomer
          }, _columnWebId));
        } else {
          actionPromise = dispatch(createNewDeal(dealBody, _columnWebId));
        }
      }

      // Wait for the async action to finish before reloading and closing
      const result = await actionPromise;
      if (result?.ok === false) {
        if (result.customerId) {
          setSelectedCustomer((prev) => ({ ...prev, id: result.customerId }));
          methods.setValue('customer_id', String(result.customerId), { shouldDirty: true, shouldValidate: true });
        }
        return;
      }

      reloadDeals?.();
      closeModal();
    } catch (error) {
      dispatch({ type: 'LOAD_DATA_FAILURE', err: error });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (agents) {
      const data = agents.map((row) => ({ id: row.id, label: `${row.first_name} ${row.last_name}` }));

      setAgentsOptions(data);
    }
  }, [agents]);

  useEffect(() => {
    dispatch(fetchActiveAgents());
  }, []);

  const openCreateCustomer = () => {
    dispatch({
      type: SIDEBAR_ACTIONS.OPEN_SIDEBAR,
      payload: {
        componentKey: 'CreateCustomer',
        props: {
          onCreateSuccess: (customer) => {
            setSelectedCustomer(customer);
            methods.setValue('customer_id', '0', { shouldDirty: true, shouldValidate: true });
          }
        }
      }
    });
  };

  const funnelStepId = methods.watch('funnel_step_id');
  const currentFunnel = funnels.find(f => String(f.id) === String(selectedFunnelId));

  useEffect(() => {
    const selectedStep = currentFunnel?.funnel_steps?.find((step) => String(step.id) === String(funnelStepId));

    if (selectedStep) {
      const reasonsOptions = selectedStep.reasons?.map((reason) => ({
        id: reason.id,
        label: reason.reason
      }) || []);

      // Only set the close reason if it exists in the new options
      const validReason = reasonsOptions.some(r => String(r.id) === String(deal?.reason_closure_id));
      if (validReason) {
        methods.setValue('close_reason', deal?.reason_closure_id, { shouldDirty: true, shouldValidate: true });
      } else {
        methods.setValue('close_reason', '', { shouldDirty: true, shouldValidate: true });
      }
      setCloseReasons(reasonsOptions);
    } else {
      methods.setValue('close_reason', '', { shouldDirty: true, shouldValidate: true });
      setCloseReasons([]);
    }
  }, [funnelStepId]);

  useEffect(() => {
    if (currentFunnel?.funnel_steps) {
      const newOptions = currentFunnel.funnel_steps.map((step) => ({
        id: step.id,
        label: step.name
      }));
      setOptionFunnelSteps(newOptions);

      // Prefer deal.funnel_step_id if it exists in the selected funnel, otherwise set first step
      let stepIdToSet = currentFunnel?.funnel_steps[0]?.id || '';
      if (
        deal?.funnel_step_id &&
        currentFunnel.funnel_steps.some(s => String(s.id) === String(deal.funnel_step_id))
      ) {
        stepIdToSet = deal.funnel_step_id;
      }
      methods.setValue('funnel_step_id', stepIdToSet, { shouldDirty: true, shouldValidate: true });
    }
  }, [selectedFunnelId]);

  const getDefaultLabelCustomer = () => {
    if (deal && deal?.customer?.id && deal?.customer?.id !== '0' && deal?.customer?.id !== '') {
      return getLabelCustomer(deal?.customer) || t('deal.customer.placeholder');
    }

    const values = methods.getValues();
    if (selectedCustomer && values.customer_id === '0') {
      return getLabelCustomer(selectedCustomer) || t('deal.customer.placeholder');
    }

    return t('deal.customer.placeholder');
  };

  return (
    <RightModalLayout
      title={isEditing ? t('deal.titleEdit') : t('deal.title')}
      submitAction={methods.handleSubmit(submitAction)}
      cancelAction={closeModal}
      submitLabel={isEditing ? t('deal.save') : t('deal.add')}
      cancelLabel={t('deal.cancel')}
      submitDisabled={submitting}
      footerVariant="HORIZONTAL"
      footerClassName="tw-border-none"
      closeAction={closeModal}
    >
      <FormProviderMC methods={methods}>
        {(isEditing || externalCreate) && (
          <DropdownMC
            useFloatingLabel
            name="funnel_id"
            label={t('deal.funnel.title')}
            options={funnels.map(f => ({ id: f.id, label: f.name }))}
            className="tw-mb-4"
            defaultValue={selectedFunnelId}
            onSelect={id => {
              setSelectedFunnelId(id);
            }}
            placeholder={t('deal.funnel.placeholder')}
          />
        )}
        <DropdownMC
          useFloatingLabel
          name="funnel_step_id"
          label={t('deal.stage.title')}
          options={optionFunnelSteps}
          className="tw-mb-4"
          defaultValue={methods.getValues('funnel_step_id')}
          placeholder={t('deal.stage.placeholder')}
        />
        {!externalCreate && (
          <div className="tw-flex tw-items-center tw-mb-4">
            <DropdownAsyncMC
              useFloatingLabel
              name="customer_id"
              label={t('deal.customer.title')}
              className="tw-flex-1"
              defaultValue={methods.getValues('customer_id')}
              placeholder={getDefaultLabelCustomer()}
              loadOptions={loadOptions}
            />
            {!isEditing && !externalCreate && (
              <ButtonComponent
                icon="plus-outline"
                iconClassName="tw-text-xl"
                className="tw-ml-2 tw-self-stretch"
                onClick={openCreateCustomer}
              />
            )}
          </div>
        )}
        <InputComponent
          useFloatingLabel
          name="name"
          maxLength={100}
          label={t('deal.negotiation.title')}
          placeholder={t('deal.negotiation.placeholder')}
          className="tw-mb-4"
        />
        <InputComponent
          useFloatingLabel
          name="amount"
          label={t('deal.amount.title')}
          placeholder={t('deal.amount.placeholder')}
          className="tw-mb-4"
          type="number"
        />
        <DatePickerMC
          showMonthDropdown
          showYearDropdown
          label={t('deal.openingDate.title')}
          dateFormat="dd-MM-yyyy"
          name="opening_date"
          placeholder={t('deal.openingDate.placeholder')}
          className="tw-mb-4"
        />
        <DatePickerMC
          showMonthDropdown
          showYearDropdown
          label={t('deal.expectedCloseDate.title')}
          dateFormat="dd-MM-yyyy"
          name="expected_close_date"
          placeholder={t('deal.expectedCloseDate.placeholder')}
          className="tw-mb-4"
        />
        <DropdownMC
          useFloatingLabel
          name="retailer_user_id"
          label={t('deal.agent.title')}
          options={agentsOptions}
          className="tw-mb-4"
          defaultValue={deal?.agent?.id || deal?.retailer_user_id || (isEditing ? '' : currentRetailerUser?.id)}
          placeholder={t('deal.agent.placeholder')}
          search
        />
        <RadioButtonSelectorMC
          name="lead_status"
          className="tw-mb-4"
          label={t('deal.temperature.title')}
          options={Object.entries(tempClasses).map(([key, value]) => ({
            label: t(`deal.temperature.${key}`),
            value: key,
            icon: { iconName: 'thermometer-outline', size: 'sm', className: value }
          }))}
        />
        {closeReasons?.length > 0 && (
          <div key={funnelStepId || 'default'}>
            <TitleMC text={t('deal.closeReason.title')} variant="SECTION" />
            <DropdownMC
              useFloatingLabel
              name="close_reason"
              label={t('deal.closeReason.title')}
              options={closeReasons}
              className="tw-mb-4"
              defaultValue={methods.getValues('close_reason')}
              placeholder={t('deal.closeReason.placeholder')}
            />
          </div>
        )}
      </FormProviderMC>
    </RightModalLayout>
  );
};

export default ManageDeal;
